import { ChangeEvent, useEffect, useState } from "react";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { InsuranceFormState } from "../../types/insurance.ts";
import { normalizeInsuranceCompanyProperties } from "../../utils/NormalizationUtil.ts";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const InsuranceCompanyForm = () => {
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [baseInsuranceCompanies, setBaseInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});

  const NEW_INSURANCE_COMPANY_OBJECT = { 0: "Нов запис" };

  const DEFAULT_FORM_STATE: InsuranceFormState = {
    selectedMainCompany: "",
    selectedInsuranceCompany: "0",
    name: "",
    phone: "",
    reportNumber: 0,
    address: "",
    otherData: "",
    isActive: true,
    isInReport: true,
  };

  const [formState, setFormState] =
    useState<InsuranceFormState>(DEFAULT_FORM_STATE);
  const { jwt } = useAuth();

  useEffect(() => {
    const fetchInsuranceCompanies = async () => {
      try {
        const allBaseInsuranceCompanies =
          await InsuranceService.getBaseInsuranceCompanies(jwt);
        setBaseInsuranceCompanies(
          allBaseInsuranceCompanies as { [key: string]: string }
        );
      } catch (err: any) {
        throw new Error(
          "Грешка при зареждане на застрахователни компании:",
          err.message
        );
      }
    };
    fetchInsuranceCompanies();
  }, [jwt]);

  useEffect(() => {
    const fetchInsuranceCompaniesByBaseCompany = async () => {
      const allInsuranceCompanies =
        await InsuranceService.getAllInsuranceCompanies(
          jwt,
          true,
          formState.selectedMainCompany
        );
      setInsuranceCompanies({
        ...NEW_INSURANCE_COMPANY_OBJECT,
        ...(allInsuranceCompanies as { [key: string]: string }),
      });
    };
    if (formState.selectedMainCompany) fetchInsuranceCompaniesByBaseCompany();
  }, [formState.selectedMainCompany]);

  useEffect(() => {
    if (formState.selectedInsuranceCompany !== "0") {
      fetchAgencyData(formState.selectedInsuranceCompany);
    } else {
      setFormState((prev) => ({
        ...prev,
        name: "",
        phone: "",
        reportNumber: 0,
        address: "",
        otherData: "",
        isActive: true,
        selectedMainCompany: "",
      }));
    }
  }, [formState.selectedInsuranceCompany]);

  const fetchAgencyData = async (companyId: string) => {
    try {
      const agencyData = await InsuranceService.getAgencyDataByCompany(
        jwt,
        companyId
      );
      setFormState((prev) => ({
        ...prev,
        name: agencyData.companyName || "",
        phone: agencyData.phone || "",
        reportNumber: agencyData.reportNumber || 0,
        address: agencyData.address || "",
        otherData: agencyData.otherData || "",
        isActive: agencyData.isActive === 1,
        isInReport: agencyData.isInReport === 1,
        selectedMainCompany: agencyData.baseInsuranceCompany || "",
      }));
    } catch (err: any) {
      throw new Error("Error fetching agency data:", err.message);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    const formData = {
      companyId: formState.selectedInsuranceCompany,
      baseInsuranceCompany: formState.selectedMainCompany,
      companyName: formState.name,
      address: formState.address,
      phone: formState.phone,
      reportNumber: formState.reportNumber,
      isInReport: formState.isInReport ? 1 : 0,
      isActive: formState.isActive ? 1 : 0,
      otherData: formState.otherData,
    };
    const normalizedFormData = normalizeInsuranceCompanyProperties(
      formData,
      true
    );
    try {
      await InsuranceService.writeAgencyData(jwt, {
        agencyData: { ...normalizedFormData },
      });
      setFormState(DEFAULT_FORM_STATE);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Застрахователни компании" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <SectionHeader title="Застрахователни компании" />
        <div className="p-3">
          {/* Row 1 - company selects + name */}
          <div className="mb-2 grid grid-cols-3 gap-2">
            <div>
              <label className={labelClass}>Застрахователна компания</label>
              <SelectGroup
                value={formState.selectedMainCompany}
                placeholder="Изберете компания"
                options={baseInsuranceCompanies || {}}
                shouldSort={false}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    selectedMainCompany: value,
                  }))
                }
              />
            </div>
            <div>
              <label className={labelClass}>Застрахователна агенция</label>
              <SelectGroup
                value={formState.selectedInsuranceCompany}
                placeholder="Изберете компания"
                options={insuranceCompanies || {}}
                shouldSort={false}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    selectedInsuranceCompany: value,
                  }))
                }
              />
            </div>
            <div>
              <label className={labelClass}>Наименование</label>
              <input
                type="text"
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 2 - phone, report number, address */}
          <div className="mb-2 grid grid-cols-3 gap-2">
            <div>
              <label className={labelClass}>Телефон</label>
              <input
                type="text"
                name="phone"
                value={formState.phone}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Номер Отчет</label>
              <input
                type="number"
                name="reportNumber"
                value={formState.reportNumber}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Адрес</label>
              <input
                data-no-decimal-guard=""
                type="text"
                name="address"
                value={formState.address}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 3 - other data */}
          <div className="mb-2">
            <label className={labelClass}>Други данни</label>
            <textarea
              data-no-decimal-guard=""
              name="otherData"
              value={formState.otherData}
              onChange={handleInputChange}
              rows={2}
              className={inputClass + " resize-none"}
            />
          </div>

          {/* Checkboxes */}
          <div className="mb-3 flex items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formState.isActive}
                onChange={handleInputChange}
                className="accent-secondary-dark h-3 w-3"
              />
              <span className="text-xs font-medium text-black dark:text-white">
                Активна
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                name="isInReport"
                checked={formState.isInReport}
                onChange={handleInputChange}
                className="accent-secondary-dark h-3 w-3"
              />
              <span className="text-xs font-medium text-black dark:text-white">
                В дневник на брокера
              </span>
            </label>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              onClick={handleSubmit}
            >
              Запис
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InsuranceCompanyForm;
